import { Record, Map, List, getIn } from 'immutable';

import { CountryList } from 'enums/country';
import { CompanyType } from 'enums/company-type';
import { FundingMatchStatus } from 'enums/funding-match-status';
import { FundingSearchStatus, toLabel } from 'enums/funding-search-status';

import Hydrator from 'utils/Hydrator';
import { formatCurrency } from 'utils/NumberHelper';

import Lender from 'models/LenderModel';
import Entity from './Entity';
import ApiUser from './ApiUser';
import TeamMemberModel from './TeamMemberModel';
import ValidationRules from './ValidationRulesModel';

const FundingSearchRecord = Record({
  id: 0,
  allMatchesCount: 0,
  amount: '',
  anonymized: false,
  client: null,
  communicationPreference: 1,
  consultant: null,
  contactTime: 0,
  createdAt: new Date(0),
  disclosureCommissionConfirmedBy: null,
  disclosureCommissionConfirmedOn: null,
  governmentBacked: false,
  introducer: null,
  isTest: false,
  lender: Map(),
  manager: null,
  matches: List(),
  notify: [],
  period: 0,
  private: false,
  validationRules: Map(),
  failingValidationRules: Map(),
  products: [],
  profile: Map(),
  profileCompletion: 0,
  requireDisclosureCommission: false,
  status: FundingSearchStatus.submitted,
  statusMatchesCount: 0,
  useOfFunds: '',
  updatedAt: new Date(0),
});

export default class FundingSearch extends FundingSearchRecord {
  static deserialize(jsonObject = {}) {
    if (typeof jsonObject === 'undefined' || jsonObject === null) {
      return undefined;
    }

    const mapProperties = {
      client: Entity.deserialize,
      introducer: Entity.deserialize,
      author: ApiUser.deserialize,
      consultant: ApiUser.deserialize,
      manager: TeamMemberModel.deserialize,
      createdAt: Hydrator.timestamp2Date,
      updatedAt: Hydrator.timestamp2Date,
      failingValidationRules: ValidationRules.deserialize,
      validationRules: ValidationRules.deserialize,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    // Finding the lender based on the matches
    const paidOutMatch = List(hydrated.matches).find(
      (match) => match.status === FundingMatchStatus.paidOut,
    );
    hydrated.lender = paidOutMatch ? Lender.deserialize(paidOutMatch.lender) : undefined;

    return new FundingSearch(hydrated);
  }

  get tradingPeriodWithUnit() {
    if (this.profile.tradingPeriod < 12) {
      return `${this.profile.tradingPeriod} ${this.profile.tradingPeriod <= 1 ? 'mth' : 'mths'}`;
    }

    const inYears = this.profile.tradingPeriod / 12;

    return `${Math.round(inYears * 10) / 10} ${inYears <= 1 ? 'yr' : 'yrs'}`;
  }

  get previewTitle() {
    return `${this.tradingPeriodWithUnit} old ${formatCurrency(this.profile.revenue)}`;
  }

  get companyName() {
    return this.client ? this.client.companyName : '';
  }

  get userLastInvitedAt() {
    return this.client?.userLastInvitedAt || 0;
  }

  get companyCountryCode() {
    return this.client?.countryCode ?? CountryList.GB;
  }

  get isSoleTrader() {
    return this.client?.companyType === CompanyType.soleTrader;
  }

  get title() {
    return this.anonymized ? this.previewTitle : this.companyName;
  }

  get statusAsLabel() {
    return toLabel(this.status);
  }

  get completionLabel() {
    if (this.profileCompletion >= 0 && this.profileCompletion < 40) {
      return 'More work to do';
    }

    if (this.profileCompletion >= 40 && this.profileCompletion < 65) {
      return 'Halfway there';
    }

    if (this.profileCompletion >= 65 && this.profileCompletion < 99) {
      return 'Almost there';
    }

    if (this.profileCompletion >= 99) {
      return 'Complete';
    }

    return '';
  }

  get isStatusPreRelease() {
    return (
      this.status === FundingSearchStatus.incomplete ||
      this.status === FundingSearchStatus.awaitingDocuments ||
      this.status === FundingSearchStatus.capitaliseReview
    );
  }

  get completion() {
    return Math.floor(this.profileCompletion);
  }

  get fundingMatchesProgress() {
    return this.matches.length ? (this.statusMatchesCount / this.allMatchesCount) * 100 : 0;
  }

  get productType() {
    return this.lender.productType;
  }

  get formattedAmount() {
    return this.amount ? formatCurrency(this.amount) : '-';
  }

  get formattedPeriod() {
    return this.period ? `${this.period} Months` : '-';
  }

  getFailingRulesForSectionType(type) {
    return this.failingValidationRules.get(type, []);
  }

  getValidationRulesForSectionType(type) {
    return this.validationRules.get(type, []);
  }

  getMatchByEntityId(entityId) {
    return List(this.matches).find((match) => getIn(match, ['lender', 'entityId']) === entityId);
  }

  hasOffersByEntityId(entityId) {
    return getIn(this.getMatchByEntityId(entityId), ['offer', 'id'], false);
  }
}
